// MarketplaceEmbed — top-level embed component.
//
// Provides: JWT auth flow, cart context, in-memory routing for catalog /
// PDP / cart / checkout / account views, with the catalog⇄PDP pair synced to
// the URL hash (D6, Task 12): `#/p/<slug>` ⇄ {page:"product", slug}; absent
// or unknown hash ⇄ catalog (graceful — never an error, never rewritten on
// mount). Only those two views participate: cart/checkout/order/account are
// personal transient states and stay out of the URL. Resets font-family,
// box-sizing, and line-height to neutralize Framer host CSS leak.
//
// Theme migration (TD-269 Sub-project D): the root chrome is wrapped in
// <EmbedThemeProvider> and every component — root and children (ProductGrid,
// CartReviewView, AccountCenter, ...) — reads colors via useEmbedTokens().
// The back-compat TOKENS shim has been removed; all consumers are theme-aware.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { AuthProvider, useAuth } from "../../embed-shared/auth-context";
import { CartProvider, useCart } from "../../embed-shared/cart-context";
import { API_BASE_URL, ApiError } from "../../embed-shared/api-client";
import {
  EmbedThemeProvider,
  useEmbedTokens,
  EmbedBanner,
  EmbedButton,
} from "../../embed-shared/primitives";
import type { CatalogFacets, Product } from "../../embed-shared/types";
import { OrderConfirmation } from "./OrderConfirmation";
import type { SectionId } from "./account-sections";
import { AccountCenter } from "./AccountCenter";
import { ReferralLeadPanel } from "./ReferralLeadPanel";
import { CartReviewView } from "./CartReviewView";
import { CatalogView, type CatalogSort } from "./CatalogView";
import { ProductDetailView } from "./ProductDetailView";
import { CartView } from "./CartView";
import { CheckoutFlow } from "./CheckoutFlow";
import { StorefrontShell } from "./StorefrontShell";
import { StorefrontConfigProvider } from "./storefront-config";
import { SignInOverlay } from "./SignInOverlay";
import { acceptInvitation, getSelfView } from "./membership-api";
import { SHARE_LINK_TOKEN_SHAPE } from "./referral-api";

// -- Route type ---------------------------------------------------------------

export type Route =
  | { page: "catalog" }
  | { page: "product"; slug: string }
  | { page: "cart" }
  | { page: "checkout" }
  | { page: "cart-review"; cartId: string }
  /** Task 17: carries the placed order's id so the confirmation can fetch its
   *  own snapshot (GET /me/orders/{orderId}); number + email are the hero's
   *  failure-proof strings from the checkout response. */
  | { page: "order-confirmation"; orderId: string; orderNumber: string; email: string }
  | { page: "order-detail"; orderId: string }
  /** `section` deep-links a mount (Task 17's Refer & Save CTA feeds "refer").
   *  Absent = the shell's default. AccountCenter itself falls back to
   *  DEFAULT_SECTION when the section is not visible to this member. */
  | { page: "account"; section?: SectionId }
  /** Task 20 slice 2: the attributed share link's PUBLIC landing. A mount
   *  opened as {mount}?ref=<token> starts here — an anonymous visitor (the
   *  referred store's contact), so needsAuth must NEVER cover this route.
   *  The token rides verbatim into the public lead POST; the URL param is
   *  read at mount and is NOT stripped (a refresh re-opens the landing —
   *  unlike the invitation flow, the link's whole purpose is re-arrival). */
  | { page: "referral-lead"; refToken: string };

/** Remove the ?token= invitation param from the page URL without touching
 *  anything else (hash included — a #/p/<slug> deep link must survive).
 *  replaceState, not pushState: stripping a consumed/failed invitation token
 *  is not a navigation the Back button should replay. */
function stripInviteTokenParam(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("token")) return;
  params.delete("token");
  const qs = params.toString();
  window.history.replaceState(
    null,
    "",
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
  );
}

/** The invitation-acceptance flow's terminal outcome, rendered as a banner
 *  above the route content. "failed" carries the server's refusal VERBATIM
 *  — the uniform 404 / already-member 409 messages ARE the flow's outcomes,
 *  not errors to reinterpret. */
type AcceptanceState =
  | { status: "idle" }
  | { status: "accepted"; role: string; accountName: string | null; notice?: string }
  | { status: "failed"; message: string };

// -- Inner router component ---------------------------------------------------

/** The embed's ONLY URL-participating route shape (D6): #/p/<slug>. */
const PRODUCT_HASH_RE = /^#\/p\/([^/]+)$/;

/**
 * Parse the page URL's hash into a Route (D6 read side). Returns the product
 * route for a `#/p/<slug>` hash; null for anything else — absent, unknown,
 * or malformed — which callers treat as "catalog" (graceful degrade: an
 * unknown hash is never an error and is never rewritten).
 */
function routeFromHash(): Route | null {
  const m = PRODUCT_HASH_RE.exec(window.location.hash);
  if (!m) return null;
  try {
    return { page: "product", slug: decodeURIComponent(m[1]!) };
  } catch {
    // Malformed percent-escape in the hash — degrade like any unknown hash.
    return null;
  }
}

/**
 * Task 20 slice 2: parse the page URL's `?ref=` into the PUBLIC landing
 * route. Null when absent, EMPTY, or not of the minted token shape (a bare
 * `?ref=` or `?ref=newsletter` is not a link — degrade to the normal mount,
 * never an error). URLSearchParams.get already
 * percent-decodes, so the token arrives verbatim. The invitation flow's
 * `?token=` is the precedent for reading a param from the mount page; unlike
 * it, this param is never stripped — the landing re-opens on refresh.
 */
function referralLeadFromQuery(): Route | null {
  const ref = new URLSearchParams(window.location.search).get("ref");
  // Only a value of the MINTED token shape is a share link (I-2): `ref=` is a
  // common campaign parameter, and routing any non-empty value here would
  // hijack the catalog for `?ref=newsletter`.
  if (!ref || !SHARE_LINK_TOKEN_SHAPE.test(ref)) return null;
  return { page: "referral-lead", refToken: ref };
}

function MarketplaceRouter({
  initialView,
  recaptchaSiteKey,
  passkeyEligible,
}: {
  initialView?: Route;
  recaptchaSiteKey?: string;
  passkeyEligible?: boolean;
}) {
  const t = useEmbedTokens();
  const { token, setToken, clearToken } = useAuth();
  const { clearCart, addLine } = useCart();

  // D6: the hash wins over initialView — a shared #/p/<slug> deep link opens
  // the PDP directly. Parsed inside the useState initializer so the FIRST
  // render already lands on the deep-linked view (no catalog flash). The
  // ?ref= landing outranks the hash: a share link's whole purpose is landing
  // its recipient on the lead form, whatever else the URL carries.
  const [route, setRoute] = useState<Route>(
    () => referralLeadFromQuery() ?? routeFromHash() ?? initialView ?? { page: "catalog" },
  );

  // Operator item (b), 2026-08-16: sign-in initiated FROM the cart returns to
  // the cart after auth — only a toolbar sign-in initiated elsewhere lands on
  // My Account (the operator's rule). Set when the toolbar Sign in is clicked
  // while on the cart; consumed by the post-auth effect below.
  const [signInReturnTo, setSignInReturnTo] = useState<Route | null>(null);

  // -- Invitation acceptance (ACL P6, plan §1.4) -------------------------------
  //
  // The invitation email lands on the marketplace mount as {base}?token={raw}
  // (account_invitation_service.go:604-608; prod's ACCOUNT_INVITATION_ACCEPT_
  // BASE_URL IS the mount). The URL is the ONLY carrier (rule 7 — the token
  // is NEVER persisted): unauthenticated visitors are routed to the gated
  // account view so SignInOverlay shows, and because sign-in happens IN
  // PLACE (no navigation) the ?token= survives the auth round-trip with no
  // carrier at all — the same property the sign-in-return mechanism rests
  // on. Once a JWT exists the raw token is POSTed; the param is stripped on
  // BOTH outcomes because the flow is terminal (the email link is the retry
  // path after a refusal).
  const [acceptance, setAcceptance] = useState<AcceptanceState>({ status: "idle" });
  // The accept POST fires at most once per page load. A re-run of this effect
  // (the token landing after sign-in, or a future token refresh) must never
  // re-POST a consumed token into a second banner.
  const acceptFiredRef = useRef(false);

  useEffect(() => {
    const inviteToken = new URLSearchParams(window.location.search).get("token");
    if (!inviteToken) return;
    if (!token) {
      // Unauthenticated: show the sign-in overlay by routing to a gated view.
      // Functional setRoute — this effect's deps are [token], so `route` here
      // would be stale on the post-sign-in re-run.
      setRoute((prev) => (prev.page === "account" ? prev : { page: "account" }));
      return;
    }
    if (acceptFiredRef.current) return;
    acceptFiredRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await acceptInvitation(inviteToken);
        // Plan §1.4 "show role+account": the accept response carries the
        // membership's role but NOT the account, so self-view supplies the
        // name. If that read fails the banner degrades to the role alone —
        // the accept itself already succeeded and must not look like a
        // refusal.
        let accountName: string | null = null;
        try {
          const sv = await getSelfView();
          accountName =
            sv?.account?.company_name ?? (sv?.account ? `Account #${sv.account.id}` : null);
        } catch {
          // Degrade, never fail — see above.
        }
        if (cancelled) return;
        stripInviteTokenParam();
        setAcceptance({
          status: "accepted",
          role: res.membership.role,
          accountName,
          notice: res.prospect_retained_notice,
        });
        // Land in the marketplace — not the gate's account page the overlay
        // lived on.
        setRoute({ page: "catalog" });
      } catch (err) {
        if (cancelled) return;
        stripInviteTokenParam();
        setAcceptance({
          status: "failed",
          // VERBATIM: "This invitation is no longer valid" (unknown/expired/
          // revoked/consumed — the anti-enumeration refusal) and "You are
          // already a member of an account — leave it before joining another".
          message: err instanceof ApiError ? err.message : "We couldn't accept this invitation.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Catalog filters (Task 12 search/category; Task 11 adds productType +
  // sort). Lifted here — the router owns both the shell (which renders the
  // toolbar controls + active-filter chips) and the catalog route (which
  // consumes them via CatalogView + renders the facet rail) — and persisted
  // across route changes so a filter survives a trip to a PDP and back.
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [productType, setProductType] = useState<string | null>(null);
  const [sort, setSort] = useState<CatalogSort>("featured");

  // Last-loaded catalog FIRST page, lifted via CatalogView.onCatalogLoaded
  // (Task 11), kept populated across route changes so it survives catalog →
  // PDP → back.
  //
  // Was `heroProducts`: it fed the shell's D10 hero montage. The 2026-08-16
  // redesign removed that montage (and the From-$ chip before it), so the
  // PRODUCTS half now has exactly one consumer — ProductDetailView's
  // related-products rail — and the name says so (§48). `heroFacets` keeps its
  // name because it genuinely still feeds the hero's sibling: the shell's
  // category-chip label resolution.
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [heroFacets, setHeroFacets] = useState<CatalogFacets | null>(null);
  // Stable identity (empty deps): CatalogView deliberately treats
  // onCatalogLoaded as a reporting channel, not an effect input — but keeping
  // the callback stable also guards against any future dependency-creep
  // reintroducing a refetch loop.
  const handleCatalogLoaded = useCallback((products: Product[], facets: CatalogFacets | null) => {
    setCatalogProducts(products);
    setHeroFacets(facets);
  }, []);

  // Task 5: the standalone order-detail fetch effect is GONE — the
  // order-detail route now mounts AccountCenter (initialSection="orders",
  // initialOrderId), and AccountOrderDetail owns the GET /me/orders/{id}
  // fetch, rendered inside the account shell below the section nav.

  // D6 write side: PROGRAMMATIC route changes write the hash; mount never
  // does (a junk hash like #/nonsense stays as typed — least surprise). Only
  // catalog and product participate, and writes only ever target the embed's
  // own #/p/… namespace: catalog→product PUSHes (browser Back returns to the
  // catalog), product→product is a same-view correction and REPLACEs (no
  // history spam), and a programmatic return to the catalog clears a #/p/…
  // hash via replaceState (no extra history entry, no hashchange).
  const prevRouteRef = useRef<Route | null>(null);
  useEffect(() => {
    const prev = prevRouteRef.current;
    prevRouteRef.current = route;
    if (prev === null) return; // mount — never rewrite the hash

    if (route.page === "product") {
      const desired = `#/p/${route.slug}`;
      if (window.location.hash === desired) return;
      if (prev.page === "product") {
        window.history.replaceState(null, "", window.location.pathname + window.location.search + desired);
      } else {
        // Hash assignment pushes a history entry (and fires hashchange —
        // the read-side listener no-ops on it because the route already
        // matches).
        window.location.hash = desired;
      }
      return;
    }
    if (
      route.page === "catalog" &&
      prev.page === "product" &&
      PRODUCT_HASH_RE.test(window.location.hash)
    ) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, [route]);

  // D6 read side: hashchange drives the route — the browser Back/Forward
  // path (and a user-edited URL). A hash parsing to the CURRENT product
  // route is a no-op (idempotence against the write side above); a cleared
  // hash pops a product view back to the catalog; catalog (and the
  // non-participating personal pages) are left untouched by a null parse.
  useEffect(() => {
    function onHashChange() {
      const parsed = routeFromHash();
      if (parsed) {
        // Idempotence against the write side above: the hash a programmatic
        // push wrote parses back to the route we are already on.
        if (parsed.page === "product" && route.page === "product" && parsed.slug === route.slug) {
          return;
        }
        setRoute(parsed);
        return;
      }
      if (route.page === "product") setRoute({ page: "catalog" });
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [route]);

  // Forwards addLine's explicit { ok, error? } result (Task 15 fix) — do NOT
  // swallow it and return void. ProductDetailView branches its add-to-cart
  // confirmation/alert on this return value rather than re-reading cart
  // state after the fact, which is not safely orderable against this call
  // (see AddLineResult's doc comment in cart-context.tsx).
  // Device-count unit (Task 9): the optional storeId rides the Task-7
  // cart-add wire when the customer picked/quick-added a store on the PDP.
  const handleAddToCart = useCallback(
    (productId: string, opts?: { storeId?: string; newLine?: boolean }) =>
      addLine(productId, 1, opts),
    [addLine],
  );

  // -- Auth gate (Task 13, fixes spec defect D3) -------------------------------
  //
  // Routes that require a token. `cart` is deliberately NOT here: Task 11
  // made the cart guest-capable (a sessionStorage staging buffer), so it must
  // never show the sign-in gate — see isGuest on useCart().
  const needsAuth =
    route.page === "checkout" || route.page === "account" || route.page === "order-detail";

  // Task 11: true while SignInPanel is showing the post-signup
  // passkey-enrollment offer. token is ALREADY truthy at that point (the
  // customer genuinely finished OTP verification — SignInPanel calls the
  // real onSignIn immediately, never deferred), but the overlay must stay
  // mounted one extra beat so the offer is actually visible: without this,
  // the token flip alone would flip `gated` false and unmount SignInOverlay
  // (and SignInPanel inside it) before the customer ever saw the prompt.
  // Set/cleared via SignInOverlay's onEnrollPromptActiveChange, which
  // forwards SignInPanel's own signal (see that file's finishOtpSignIn).
  const [pendingEnrollOffer, setPendingEnrollOffer] = useState(false);
  const gated = (!token && needsAuth) || pendingEnrollOffer;

  // Regression (found in the Task 5 review): the pre-extraction guard was an
  // early return inside this component, which itself never unmounts — so all
  // 12 checkout states survived it. After Task 5 moved that state into
  // CheckoutFlow, the same early return started destroying it: a 401 on the
  // order POST (api-client.ts:158-164 -> auth-context.tsx clearToken) dropped
  // the shipping address, billing selection, card details, and error banner,
  // landing the user back at the address step.
  //
  // The fix: a route holding unsaved user input keeps its state behind an
  // overlay; a read-only route is replaced. checkout is the only gated route
  // holding unsaved input, so it alone stays mounted. account/order-detail
  // hold nothing worth preserving, and keeping them mounted while gated would
  // fire their /me/* fetches unauthenticated — so they are replaced exactly
  // as before.
  //
  // hasAuthedRef additionally guards checkout against mounting for a visitor
  // who has NEVER been authenticated in this session. That case is newly
  // reachable now that cart no longer gates: a guest can stage cart lines and
  // click "Proceed to checkout" without ever signing in, landing on the
  // checkout route while gated. Content behind the overlay is inert, so a
  // never-authenticated visitor cannot have entered anything into checkout
  // yet — there is nothing to preserve — and mounting CheckoutFlow anyway
  // would fire AddressSelector's mount-time listAddresses() call while
  // unauthenticated, violating "do not fire fetches from behind the overlay".
  // Once token has been truthy at least once, hasAuthedRef stays true for the
  // life of this component, so the actual regression scenario (a LATER gate
  // event on an already-populated checkout) always preserves state.
  const hasAuthedRef = useRef(!!token);
  useEffect(() => {
    if (token) hasAuthedRef.current = true;
  }, [token]);
  const preserveState = gated && route.page === "checkout" && hasAuthedRef.current;

  // Operator item (b), 2026-08-16: once auth lands, honor a pending return-to
  // (set by the toolbar Sign in while on the cart) instead of staying on the
  // account page the gate lives on. One-shot: cleared on use.
  useEffect(() => {
    if (token && signInReturnTo) {
      setRoute(signInReturnTo);
      setSignInReturnTo(null);
    }
  }, [token, signInReturnTo]);

  // Keep the covered route content in sync with an imperative `.inert`
  // assignment (rather than a JSX prop) because @types/react in this
  // toolchain does not yet type `inert` as an HTMLAttribute; the DOM
  // property itself is supported (lib.dom.d.ts) and is the mechanism the
  // brief calls for — it excludes the subtree from both focus and the
  // accessibility tree in one step, which a manual focus trap would only
  // approximate. `aria-hidden` is set alongside it for AT engines that don't
  // yet honor `inert` and for reliable testing under jsdom.
  const contentRef = useRef<HTMLDivElement>(null);
  // useLayoutEffect (not useEffect) so .inert is set BEFORE the browser paints
  // (TD-313): aria-hidden is applied during render, but a passive useEffect
  // runs after paint, leaving one painted frame where the covered subtree is
  // aria-hidden yet still focusable. useLayoutEffect runs synchronously after
  // DOM mutation and before paint, closing that window. Without `inert` support
  // (pre-2022 browsers) aria-hidden alone would leave the form tabbable; inert
  // is supported in every evergreen browser and the embed's customer-site floor
  // is overwhelmingly modern, so this is the proportionate fix rather than a
  // full focus-trap.
  useLayoutEffect(() => {
    if (contentRef.current) {
      contentRef.current.inert = gated;
    }
  }, [gated]);

  function renderRoute() {
    switch (route.page) {
      case "catalog":
        return (
          <CatalogView
            search={search}
            category={category}
            productType={productType}
            sort={sort}
            onCategoryChange={setCategory}
            onProductTypeChange={setProductType}
            onProductClick={(slug) => setRoute({ page: "product", slug })}
            onClearFilters={() => {
              setSearch("");
              setCategory(null);
              setProductType(null);
            }}
            onCatalogLoaded={handleCatalogLoaded}
          />
        );

      case "product":
        return (
          <ProductDetailView
            slug={route.slug}
            // Related-products primary source (spec §7.4): the router-lifted
            // last-loaded catalog page — populated on a catalog→PDP trip,
            // empty on a #/p/<slug> deep-link cold load, where the PDP's
            // one-shot fallback fetch takes over.
            catalogProducts={catalogProducts}
            onOpenProduct={(slug) => setRoute({ page: "product", slug })}
            onBack={() => setRoute({ page: "catalog" })}
            onAddToCart={handleAddToCart}
            // Task 10 (design §5): the add-again prompt's signed-out
            // "new subscription" arm — a second same-product line is not
            // representable in the guest buffer, so the customer signs in
            // and resumes. signInReturnTo brings them BACK to this product
            // (the shell's own header button keeps its cart-return rule).
            onRequireSignIn={() => {
              setSignInReturnTo(route.page === "product" ? route : null);
              setRoute({ page: "account" });
            }}
            onNotFound={() => setRoute({ page: "catalog" })}
          />
        );

      case "cart":
        return (
          <CartView
            onContinueShopping={() => setRoute({ page: "catalog" })}
            onProceedToCheckout={() => setRoute({ page: "checkout" })}
            onOpenProduct={(slug) => setRoute({ page: "product", slug })}
          />
        );

      case "checkout":
        return (
          <CheckoutFlow
            onBackToCart={() => setRoute({ page: "cart" })}
            onOrderPlaced={(orderId, orderNumber, email) =>
              setRoute({ page: "order-confirmation", orderId, orderNumber, email })
            }
          />
        );

      case "order-confirmation":
        return (
          <OrderConfirmation
            orderId={route.orderId}
            orderNumber={route.orderNumber}
            confirmationEmail={route.email}
            // The failure branch's affordance: the order opened inside the
            // account shell (same mount the order-detail route uses).
            onViewOrder={() => setRoute({ page: "order-detail", orderId: route.orderId })}
            // The promotional block's Refer & Save CTA (O8) deep-links the
            // account's Refer & Save section; a member without the referrals
            // area falls back to Overview inside AccountCenter.
            onReferShare={() => setRoute({ page: "account", section: "refer" })}
            onContinueShopping={() => setRoute({ page: "catalog" })}
          />
        );

      case "order-detail":
        // Task 5: the retired standalone full-page detail is replaced by the
        // ACCOUNT shell with the order open inside it — nav plus detail, one
        // mount. AccountOrderDetail (inside AccountCenter) owns the fetch and
        // its loading/error states; needsAuth above keeps gating this route.
        return (
          <AccountCenter
            initialSection="orders"
            initialOrderId={route.orderId}
            onLogout={() => {
              clearToken();
              clearCart();
              setRoute({ page: "catalog" });
            }}
            // Redesign Task 6 (Task-5 gate advisory 3 — mount parity): this
            // mount omitted onBrowseProducts, so its Overview's empty-orders
            // "Browse products" action rendered a dead control while the
            // "account" mount below routed it. Same wiring, same comment.
            onBrowseProducts={() => setRoute({ page: "catalog" })}
            // Fix-round-2 I-1: was omitted entirely, so SecurityPanel's own
            // (previously ungated) Add-a-Passkey ceremony rendered fully
            // enabled on every mount, including ones that are not the
            // aldelo.com relying party — see AccountCenterProps' doc comment.
            passkeyEligible={passkeyEligible}
          />
        );

      case "cart-review":
        return (
          <CartReviewView
            cartId={route.cartId}
            apiBase={API_BASE_URL}
            onContinueToCheckout={() => {
              // After email confirmation, route to login so the customer
              // authenticates before the existing checkout flow takes over.
              // The prospect-to-buyer flip is handled automatically by
              // OrderService.Checkout (commit 3edf689).
              setRoute({ page: "catalog" });
              // Show sign-in by navigating to a protected route. `cart` no
              // longer gates (Task 11/13 — it is guest-capable), so this
              // routes to "checkout" instead — still covered by the gate
              // above (needsAuth) — landing the customer directly in the
              // checkout flow the comment above describes, once signed in.
              setRoute({ page: "checkout" });
            }}
          />
        );

      case "account":
        return (
          <AccountCenter
            // Task 17: a deep-linked section (the confirmation's Refer & Save
            // CTA feeds "refer"). Seeded on mount only — AccountCenter falls
            // back to its default when the member cannot see the section.
            initialSection={route.section}
            onLogout={() => {
              clearToken();
              clearCart();
              setRoute({ page: "catalog" });
            }}
            // Task 5: the Orders section's empty state routes to the catalog.
            onBrowseProducts={() => setRoute({ page: "catalog" })}
            // Fix-round-2 I-1: was omitted entirely, so SecurityPanel's own
            // (previously ungated) Add-a-Passkey ceremony rendered fully
            // enabled on every mount, including ones that are not the
            // aldelo.com relying party — see AccountCenterProps' doc comment.
            passkeyEligible={passkeyEligible}
          />
        );

      case "referral-lead":
        // Task 20 slice 2: the PUBLIC landing — deliberately NOT in the
        // needsAuth set above (the visitor is the referred store's anonymous
        // contact). The reCAPTCHA site key is the mount's runtime data-config
        // value, the existing OTP precedent.
        return (
          <ReferralLeadPanel refToken={route.refToken} recaptchaSiteKey={recaptchaSiteKey} />
        );

      // Defensive runtime fallback so renderRoute always returns a value.
      // NOTE: this default is NOT a TypeScript exhaustiveness guard — its
      // presence actually SUPPRESSES compile-time exhaustiveness checking
      // (a new Route variant added to the union will silently fall through
      // here and render null instead of erroring). The real parity guard
      // lives in MarketplaceEmbed.routes.test.tsx, whose `RouteByPage` mapped
      // type over Route["page"] fails typecheck unless every variant has a
      // row. If you add a new route, add a `case` here AND a row there.
      default:
        return null;
    }
  }

  return (
    <StorefrontShell
      onNavigateCatalog={() => setRoute({ page: "catalog" })}
      onNavigateCart={() => setRoute({ page: "cart" })}
      onNavigateAccount={() => setRoute({ page: "account" })}
      onSignIn={() => {
        // Operator item (b): remember the cart so post-auth returns there;
        // a sign-in clicked anywhere else lands on My Account as before.
        setSignInReturnTo(route.page === "cart" ? route : null);
        setRoute({ page: "account" });
      }}
      search={search}
      onSearchChange={setSearch}
      category={category}
      onCategoryChange={setCategory}
      sort={sort}
      onSortChange={setSort}
      productType={productType}
      onProductTypeChange={setProductType}
      facets={heroFacets}
      // Item (q), 2026-08-16: the shell hides the hero CTA on the catalog.
      // Fed from the SAME `route` state renderRoute() switches on above — not
      // a separately-tracked "current view" — so the hero can never disagree
      // with what is actually rendered below it.
      currentPage={route.page}
    >
      {/* Invitation-acceptance outcome (ACL P6): above the route content and
          outside the gate wrapper, so it stays visible after "land in the
          marketplace" routes to the catalog. EmbedBanner carries
          role=status/alert; the dismiss button resets to idle. */}
      {acceptance.status !== "idle" && (
        <div style={{ marginBottom: t.spacing.md }}>
          {acceptance.status === "accepted" ? (
            <EmbedBanner tone="success">
              {`You've joined ${acceptance.accountName ?? "the account"} as ${acceptance.role.charAt(0).toUpperCase() + acceptance.role.slice(1)}.`}
              {acceptance.notice ? ` ${acceptance.notice}` : ""}
            </EmbedBanner>
          ) : (
            <EmbedBanner tone="error">{acceptance.message}</EmbedBanner>
          )}
          <div style={{ marginTop: t.spacing.xs }}>
            <EmbedButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAcceptance({ status: "idle" })}
            >
              Dismiss
            </EmbedButton>
          </div>
        </div>
      )}

      {/* The gate covers this route-content area only — the toolbar (a
          StorefrontShell sibling above this node) is deliberately NOT covered,
          so it stays usable (spec defect D3: the toolbar must survive the
          gate). SignInOverlay renders IN FLOW, so this wrapper — and with it
          the mount div and the Framer Fit Content instance above it — grows to
          the panel's height.
          This wrapper carries NO height reservation, and must not grow one. It
          used to set `minHeight: gated ? 360 : undefined` because the overlay
          was `position: absolute; inset: 0` and an out-of-flow element sizes to
          its containing block rather than growing it — so the number was both
          required and permanently a guess. It went stale: measured 2026-09-05 in
          a real browser, the panel needed 448px at 1440px and 479px at 390px, so
          it scrolled inside the 360px box and painted over the store footer
          (TD-452). jsdom computes no layout, so the suite could not see it; the
          guard is a real-browser geometry assertion instead.
          While gated the content behind is taken OUT of layout rather than
          painted over — `display: none` keeps it mounted so `preserveState`
          still preserves it, without contributing the catalog's full height.
          The `gated && !preserveState ? null` branch is UNCHANGED: no route
          content enters the DOM for a customer who never authenticated.
          `position: relative` is kept for absolutely positioned descendants
          inside renderRoute(); nothing in this file anchors to it any more. */}
      <div style={{ position: "relative" }}>
        <div
          ref={contentRef}
          aria-hidden={gated ? true : undefined}
          style={gated ? { display: "none" } : undefined}
        >
          {gated && !preserveState ? null : renderRoute()}
        </div>
        {gated && (
          <SignInOverlay
            onSignIn={setToken}
            recaptchaSiteKey={recaptchaSiteKey}
            passkeyEligible={passkeyEligible}
            onDismiss={() => setRoute({ page: "catalog" })}
            enrollPromptActive={pendingEnrollOffer}
            onEnrollPromptActiveChange={setPendingEnrollOffer}
          />
        )}
      </div>
    </StorefrontShell>
  );
}

// -- Root wrapper (CSS isolation + theme provider + providers) ---------------

export interface MarketplaceEmbedProps {
  /** Override the starting route. Defaults to { page: "catalog" }. */
  initialView?: Route;
  /** reCAPTCHA v3 site key. When empty/undefined, the OTP flow sends an
   *  empty token; the service verifies when configured (fail-closed) and
   *  bypasses when nil (§18 nil-guard). */
  recaptchaSiteKey?: string;
  /** Theme override from the embed config. "light" | "dark". When
   *  undefined, EmbedThemeProvider resolves from the host's
   *  prefers-color-scheme (defaulting to light) — Task 2.6 makes the
   *  config-level theme optional; for now the config always provides it. */
  theme?: "light" | "dark";
  /**
   * Whether THIS MOUNT is allowed to offer passkey sign-in/enrollment,
   * resolved by the caller at RUNTIME from the mount's data-config (RULING
   * R-M, Task 11) — never inline this at build time (build-commands.md §57:
   * the shared marketplace.js bundle is ONE build served to every mount, so
   * a build-time-baked flag would behave exactly like the 2026-08-25
   * reCAPTCHA incident). Absent/malformed is the safe default — no passkey
   * UI, email-OTP remains the only path (rule §R2: OTP is never gated on
   * this feature). Threaded straight through to SignInPanel via
   * SignInOverlay; see AldeloMarketplaceEmbed.tsx and index.tsx's
   * parseConfig for where the real value originates.
   */
  passkeyEligible?: boolean;
}

// MarketplaceEmbed wraps the whole tree in EmbedThemeProvider so every
// migrated primitive + the root chrome (and, post-Tasks-2.2-2.5, every
// child) can read the active TokenSet via useEmbedTokens().
export function MarketplaceEmbed({ initialView, recaptchaSiteKey, theme, passkeyEligible }: MarketplaceEmbedProps = {}) {
  return (
    <EmbedThemeProvider themeOverride={theme}>
      <MarketplaceEmbedInner initialView={initialView} recaptchaSiteKey={recaptchaSiteKey} passkeyEligible={passkeyEligible} />
    </EmbedThemeProvider>
  );
}

// Inner shell: reads the active TokenSet for the CSS-isolation wrapper, then
// mounts the auth + cart providers and the view router.
function MarketplaceEmbedInner({ initialView, recaptchaSiteKey, passkeyEligible }: MarketplaceEmbedProps) {
  const t = useEmbedTokens();
  return (
    <div
      style={{
        fontFamily: t.fontFamily,
        boxSizing: "border-box",
        lineHeight: t.lineHeight.normal,
        color: t.colors.text,
        fontSize: t.fontSize.md,
        WebkitFontSmoothing: "antialiased",
        // The HOST owns the horizontal gap, the embed owns none of it.
        // Previously this capped itself at maxWidth:960 + margin:0 auto, so on
        // a 1380px Framer container (a 96%-width mount on a 1440 viewport) it
        // threw away 420px — 30% of the space the operator had given it — and
        // rendered a narrow centred column with dead margins either side. The
        // horizontal padding is 0 for the same reason: whatever the mount is
        // sized to (95% in Framer, say) is 100% used. Vertical padding stays —
        // it separates the embed from surrounding page content, which no host
        // width setting expresses. Guarded by MarketplaceEmbed.test.tsx
        // ("fills its host container").
        width: "100%",
        margin: 0,
        padding: `${t.spacing.md}px 0`,
      }}
    >
      <AuthProvider>
        <CartProvider>
          {/* StorefrontConfigProvider fetches the operator-configurable display
              config (currency + the shipping=$0 label) once; defaults render
              immediately and on fetch failure (TD-308, fail-open). */}
          <StorefrontConfigProvider>
            <MarketplaceRouter initialView={initialView} recaptchaSiteKey={recaptchaSiteKey} passkeyEligible={passkeyEligible} />
          </StorefrontConfigProvider>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}
